import { ulid } from 'ulid'

// V1's cart lines are minted on the client, not fetched: a product nobody has added yet has no
// row anywhere in Vuex, so the card that renders it invents a blank line with a fresh ulid and
// quantity 0. `CategoryProductStorePairContainer` did that inline, in a computed that re-mints a
// new ulid on every access - which means a deep link cannot go looking for the blank line the
// card is holding, it has to mint its own. Both now mint through here so the shape (and the
// `-upsell` frontend_key suffix that `updateSubmittableCart` keys its upsell handling off) can
// only ever be defined once.
// Two lines are the same line when they are the same pair, configured the same way, on the same
// fulfillment day and the same side of the upsell split. Ids are strings in V1 and numbers in V2,
// and options arrive in whatever order they were picked, so both are normalized before comparing.
export function cartLineSignature(item) {
  if (!item || item.category_product_store_pair_id == null) return null
  const options = (item.cart_item_variant_options_attributes || [])
    .filter(Boolean)
    .map((row) => `${row.variant_id}:${row.variant_option_id}:${Number(row.quantity) || 1}`)
    .sort()
    .join('|')
  const isUpsell = String(item.frontend_key || '').endsWith('-upsell')
  return [String(item.category_product_store_pair_id), item.fulfillment_group_date || '', isUpsell, options].join('/')
}

/**
 * The line a new one would duplicate, if the cart already holds it.
 *
 * Before BOT-1108 the card's own stepper raised the line it belonged to, so a second identical
 * configuration was never minted. Adding through the modal mints a line per press, which printed a
 * second card identical to the first - same name, same modifiers, same price - that the customer
 * then had to reconcile by hand.
 *
 * `ceiling` is the cap the modal just enforced. Merging is skipped when the combined quantity would
 * exceed it: the backend validates max_per_order per cart item
 * (CategoryProductStoreCartItem#less_than_or_equal_to_max_per_order), so a merge can be the thing
 * that breaks a pair of lines that were each individually fine. A separate line is the honest
 * outcome there - it is also exactly what happens today.
 */
export function findMergeableCartLine(lines, candidate, { ceiling = null, addedQuantity = 0 } = {}) {
  const signature = cartLineSignature(candidate)
  if (signature == null) return null

  const match = (lines || []).find(
    (line) =>
      line &&
      line.frontend_key !== candidate.frontend_key &&
      Number(line.quantity) > 0 &&
      cartLineSignature(line) === signature
  )
  if (!match) return null

  const combined = (Number(match.quantity) || 0) + (Number(addedQuantity) || 0)
  if (ceiling != null && combined > Number(ceiling)) return null
  return { line: match, quantity: combined }
}

export function blankSubmittableCartItem({
  categoryProductStorePairId,
  fulfillmentGroupDate = null,
  isUpsell = false
} = {}) {
  return {
    frontend_key: ulid() + (isUpsell ? '-upsell' : ''),
    quantity: 0,
    fulfillment_group_date: fulfillmentGroupDate,
    category_product_store_pair_id: categoryProductStorePairId,
    cart_item_variant_options_attributes: []
  }
}
